/*
 * AaaChecker.tsx
 *
 * Created by Dr. Maximillian Dornseif 2021-11-22 in huWaWi3 24.4.0
 * Copyright (c) 2021 HUDORA GmbH
 */

import { useAuth0 } from '@auth0/auth0-react'
import { Callout } from '@fluentui/react'
import { MessageBar, MessageBarType } from '@fluentui/react/lib/MessageBar'
import gql from 'graphql-tag'
import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import ReactJson from 'react-json-view'
import { useQuery } from 'urql'
import { useLocation } from 'wouter'
import { LinkRender } from './lib/hd-link'

export function AaaMainChecker(props) {
  const [isHovering, setIsHovering] = useState(false)
  const [location] = useLocation()
  const { user } = useAuth0()
  const delayedRender = useDelayedRender()
  const path = location
    .split('/')
    .map((x) => (x.match(/\d/) ? '#' : x))
    .join('/')
  const [result] = useQuery({
    query: AAA_QUERY,
    variables: {
      path,
      sub: user?.sub,
    },
  })
  const { data } = result

  const handleMouseOver = () => {
    setIsHovering(true)
  }

  const handleDismiss = () => {
    setIsHovering(false)
  }

  if (!user) {
    return <>{props.children}</>
  }

  const buttonRoot = document.getElementById('hd-user-info-root')
  if (!buttonRoot) {
    return <>{props.children}</>
  }

  return (
    <div id="authFrame">
      {createPortal(
        <>
          <button type="button" id="hd-user-info-button" disabled onMouseOver={handleMouseOver}>
            <img height="30px" width="30px" src={user.picture} alt={user.name} />
          </button>
          <Callout
            // className={styles.callout}
            // ariaLabelledBy={labelId}
            // ariaDescribedBy={descriptionId}
            // gapSpace={0}
            hidden={!isHovering}
            target={`#hd-user-info-button`}
            onDismiss={handleDismiss}
            setInitialFocus
          >
            <div style={{ padding: '2em' }}>
              <AuthInfo info={data?.entity} path={path} />
            </div>
          </Callout>
        </>,
        buttonRoot
      )}
      {delayedRender(() => renderChildren(data, path, user, location, props))}
    </div>
  )
}

function renderChildren(data: any, path: string, user, location: string, props: any) {
  let children = props.children
  if (data?.entity?.allowed === false) {
    children = (
      <div className="authDenied">
        <MessageBar messageBarType={MessageBarType.info} isMultiline={false} truncated={true}>
          AAA{' '}
          <code>
            {JSON.stringify({
              path,
              sub: user?.sub,
              location,
            })}
          </code>
        </MessageBar>
        {props.children}
      </div>
    )
  }
  return children
}

export function AuthInfo(props) {
  return props?.info ? (
    <>
      Sie sind als <em>{props?.info?.providedUser?.name}</em>
      {' mit der Nutzerkennung '} <em>{props?.info?.providedUser?.user}</em>
      {' und dem ID '} <em>{props?.info?.providedUser?.uid}</em> angemeldet.
      <br />
      Zugriff auf <code>{props.path}</code>:<h4>Mögliche Berechtigungen</h4>
      <p>
        <small>Berechtigungen, mit denen man auf diese Seite zugreifen kann.</small>
      </p>
      <ul>
        {props?.info?.requiredPermissions.map((x) => (
          <li key={x?.designator}>
            <em>{x?.name ?? x?.subconcern}</em> (<LinkRender value={x?.designator} />, {x?.type},{' '}
            <code>{x?.subconcern}</code>)
          </li>
        ))}
      </ul>
      <h4>Benötige Rollen</h4>
      <p>
        <small>Rollen, mit denen man auf diese Seite zugreifen kann.</small>
      </p>
      <ul>
        {props?.info?.requiredRoles.map((x) => (
          <li key={x?.designator}>
            <em>{x?.name}</em> (<LinkRender value={x?.designator} />)
          </li>
        ))}
      </ul>
      <h4>Ihre Rollen</h4>
      <ul>
        {props?.info?.providedRoles.map((x) => (
          <li key={x?.designator}>
            <em>{x?.name}</em> (<LinkRender value={x?.designator} />)
          </li>
        ))}
      </ul>
      <h4>Genutzte Rollen</h4>
      <ul>
        {props?.info?.matchingRoles.map((x) => (
          <li key={x?.designator}>
            <em>{x?.name}</em> (<LinkRender value={x?.designator} />)
          </li>
        ))}
      </ul>
      <ReactJson name="Details" collapsed={true} displayObjectSize={false} src={props?.info} />
    </>
  ) : (
    <>Nicht angemeldet</>
  )
}

export const AAA_QUERY = gql`
  query AaaCheckQuery ($path: String!, $sub: String) {
    entity: aaaCheckGui(app: "${process.env.REACT_APP_NAME}", path: $path, sub: $sub) {
      allowed
      matchingRoles {
      created_at
      id
      designator
      name
      permissions
      uids
      updated_at
    }
    providedRoles {
      created_at
      id
      designator
      name
      permissions
      uids
      updated_at
    }

    providedUser {
      created_at
      enabled
      id
      designator
      name
      user
      updated_at
      uid
    }
    requiredPermissions {
      concern
      designator
      fieldName
      created_at
      fileName
      id
      ist_aktiv
      name
      operation
      returnType
      resolverName
      subconcern
      type
      updated_at
    }
    requiredRoles {
      created_at
      id
      designator
      name
      permissions
      uids
      updated_at
    }
  }
  }
`
const useDelayedRender = (delay = 10) => {
  const [delayed, setDelayed] = useState(true)
  useEffect(() => {
    const timeout = setTimeout(() => setDelayed(false), delay)
    return () => clearTimeout(timeout)
  }, [])
  return (fn) => !delayed && fn()
}
